import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Spinner from '../components/Spinner';
import RazorpayPaymentButton from '../components/RazorpayPaymentButton';
import PaymentNotification from '../components/PaymentNotification';
import { RAZORPAY_CONFIG } from '../config/razorpay';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  onSnapshot
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { 
  GoogleAuthProvider, 
  signInWithPopup 
} from 'firebase/auth';

// Date formatting utility
const formatDate = (date) => {
  if (!date) return 'Not available';
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  // Check if the date is valid
  if (isNaN(dateObj.getTime())) {
    return 'Not available';
  }
  
  const day = dateObj.getDate();
  const month = dateObj.toLocaleString('en-US', { month: 'long' });
  const year = dateObj.getFullYear();
  
  // Add ordinal suffix to day
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };
  
  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
};

// Modern animated icons
const StatusIcon = ({ status, className = "w-8 h-8" }) => {
  const getIcon = () => {
    switch (status) {
      case 'Approved':
        return (
          <div className={`${className} relative`}>
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <defs>
                <linearGradient id="approvedGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#10B981" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <circle cx="12" cy="12" r="10" fill="url(#approvedGradient)" className="animate-pulse" />
              <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'Rejected':
        return (
          <div className={`${className} relative`}>
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <defs>
                <linearGradient id="rejectedGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#EF4444" />
                  <stop offset="100%" stopColor="#DC2626" />
                </linearGradient>
              </defs>
              <circle cx="12" cy="12" r="10" fill="url(#rejectedGradient)" className="animate-pulse" />
              <path d="M15 9l-6 6M9 9l6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        );
      case 'Pending':
      default:
        return (
          <div className={`${className} relative`}>
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <defs>
                <linearGradient id="pendingGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#F59E0B" />
                  <stop offset="100%" stopColor="#D97706" />
                </linearGradient>
              </defs>
              <circle cx="12" cy="12" r="10" fill="url(#pendingGradient)" className="animate-spin" style={{animationDuration: '3s'}} />
              <circle cx="12" cy="12" r="3" fill="white" />
            </svg>
          </div>
        );
    }
  };

  return getIcon();
};

const SearchIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <defs>
      <linearGradient id="searchGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3B82F6" />
        <stop offset="100%" stopColor="#1D4ED8" />
      </linearGradient>
    </defs>
    <circle cx="11" cy="11" r="8" fill="none" stroke="url(#searchGradient)" strokeWidth="2" />
    <path d="m21 21-4.35-4.35" stroke="url(#searchGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const UserIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <defs>
      <linearGradient id="userGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#8B5CF6" />
        <stop offset="100%" stopColor="#7C3AED" />
      </linearGradient>
    </defs>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" fill="none" stroke="url(#userGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="12" cy="7" r="4" fill="none" stroke="url(#userGradient)" strokeWidth="2" />
  </svg>
);

const EmailIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <defs>
      <linearGradient id="emailGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#06B6D4" />
        <stop offset="100%" stopColor="#0891B2" />
      </linearGradient>
    </defs>
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" fill="url(#emailGradient)" />
    <polyline points="22,6 12,13 2,6" stroke="white" strokeWidth="2" fill="none" />
  </svg>
);

const PhoneIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <defs>
      <linearGradient id="phoneGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#10B981" />
        <stop offset="100%" stopColor="#059669" />
      </linearGradient>
    </defs>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" fill="url(#phoneGradient)" />
  </svg>
);

const InfoIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <defs>
      <linearGradient id="infoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#D97706" />
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="10" fill="url(#infoGradient)" />
    <line x1="12" y1="16" x2="12" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="8" r="1" fill="white" />
  </svg>
);

const CelebrationIcon = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 24 24" className={className}>
    <defs>
      <linearGradient id="celebrationGradient" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#F59E0B" />
        <stop offset="100%" stopColor="#EF4444" />
      </linearGradient>
    </defs>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="url(#celebrationGradient)" className="animate-pulse" />
    <circle cx="6" cy="6" r="1" fill="#EF4444" className="animate-bounce" style={{animationDelay: '0.1s'}} />
    <circle cx="18" cy="6" r="1" fill="#F59E0B" className="animate-bounce" style={{animationDelay: '0.2s'}} />
    <circle cx="6" cy="18" r="1" fill="#10B981" className="animate-bounce" style={{animationDelay: '0.3s'}} />
    <circle cx="18" cy="18" r="1" fill="#3B82F6" className="animate-bounce" style={{animationDelay: '0.4s'}} />
  </svg>
);

const StatusBadge = ({ status, darkMode }) => {
  const getStatusStyle = () => {
    switch (status) {
      case 'Approved':
        return 'bg-gradient-to-r from-green-400 to-green-600 text-white shadow-lg shadow-green-500/30';
      case 'Rejected':
        return 'bg-gradient-to-r from-red-400 to-red-600 text-white shadow-lg shadow-red-500/30';
      case 'Pending':
      default:
        return 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-white shadow-lg shadow-yellow-500/30';
    }
  };

  return (
    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${getStatusStyle()} transform hover:scale-105 transition-all duration-300`}>
      <StatusIcon status={status} className="w-4 h-4" />
      {status || 'Pending'}
    </div>
  );
};

// Modern Payment Component using new RazorpayPaymentButton
const PaymentSection = ({ 
  studentData, 
  onPaymentSuccess, 
  onPaymentFailure
}) => {
  return (
    <RazorpayPaymentButton
      amount={RAZORPAY_CONFIG.AMOUNTS.MBBS_SEAT_BOOKING}
      description="MBBS Seat Booking Fee"
      userInfo={{
        name: studentData?.name || '',
        email: studentData?.email || '',
        phone: studentData?.phone || '',
      }}
      onSuccess={onPaymentSuccess}
      onFailure={onPaymentFailure}
      buttonText="Pay"
    />
  );
};

const ApplicationCard = ({ 
  application, 
  darkMode, 
  onPaymentSuccess, 
  onPaymentFailure,
  user,
  viewMode
}) => {
  return (
    <div className="status-card rounded-3xl p-6 mb-6 relative overflow-hidden group border-2 border-white/20 backdrop-blur-xl shadow-2xl">
      {/* Multiple gradient overlays for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-tl from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" />
      
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-5" 
           style={{
             backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
             backgroundSize: '20px 20px',
             animation: 'float 10s ease-in-out infinite'
           }} />
      
      <div className="relative z-10">
        {/* Flex container for landscape layout on large screens */}
        <div className="flex flex-col lg:flex-row lg:gap-8 gap-6">
          {/* Student Details Section */}
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-blue-400 via-purple-500 to-purple-600 flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg ring-2 ring-white/20 flex-shrink-0">
                {application.name?.charAt(0) || 'A'}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className={`text-lg sm:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} bg-gradient-to-r ${darkMode ? 'from-white to-gray-300' : 'from-gray-900 to-gray-700'} bg-clip-text text-transparent break-words`}>
                  {application.name}
                </h3>
                <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} font-medium break-words`}>
                  Application ID: {application.applicationId}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-2 sm:space-y-3">
                <div className={`p-2 sm:p-3 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-black/5'} backdrop-blur-sm border border-white/10`}>
                  <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                    Contact Phone
                  </label>
                  <p className={`text-xs sm:text-sm ${darkMode ? 'text-white' : 'text-gray-900'} font-medium mt-1 break-words`}>
                    {application.phone || 'N/A'}
                  </p>
                </div>
                <div className={`p-2 sm:p-3 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-black/5'} backdrop-blur-sm border border-white/10`}>
                  <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                    Category
                  </label>
                  <p className={`text-xs sm:text-sm ${darkMode ? 'text-white' : 'text-gray-900'} font-medium mt-1`}>
                    {application.category || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="space-y-2 sm:space-y-3">
                <div className={`p-2 sm:p-3 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-black/5'} backdrop-blur-sm border border-white/10`}>
                  <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                    NEET Score
                  </label>
                  <p className={`text-xs sm:text-sm ${darkMode ? 'text-white' : 'text-gray-900'} font-medium mt-1`}>
                    {application.neetScore}
                  </p>
                </div>
                <div className={`p-2 sm:p-3 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-black/5'} backdrop-blur-sm border border-white/10`}>
                  <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                    Submitted
                  </label>
                  <p className={`text-xs sm:text-sm ${darkMode ? 'text-white' : 'text-gray-900'} font-medium mt-1`}>
                    {formatDate(application.submittedAt)}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Status Badge and Last Updated for mobile - better spacing */}
            <div className="flex flex-col items-center gap-3 lg:hidden mt-4">
              <StatusBadge status={application.status} darkMode={darkMode} />
              <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-green-400 to-blue-500 animate-pulse shadow-sm" />
                <span>
                  Last updated: {
                    application.updatedAt 
                      ? formatDate(application.updatedAt)
                      : application.approvedAt 
                        ? formatDate(application.approvedAt)
                        : application.rejectedAt
                          ? formatDate(application.rejectedAt)
                          : formatDate(application.submittedAt)
                  }
                </span>
              </div>
            </div>
          </div>
          
          {/* Payment Section - Beside student details on large screens */}
          <div className="lg:w-80 flex flex-col gap-4">
            {/* Status Badge and Last Updated for large screens - perfect centered alignment */}
            <div className="hidden lg:flex flex-col items-center gap-3 mb-2">
              <StatusBadge status={application.status} darkMode={darkMode} />
              <div className={`flex items-center gap-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'}`}>
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-green-400 to-blue-500 animate-pulse shadow-sm" />
                <span>
                  Last updated: {
                    application.updatedAt 
                      ? formatDate(application.updatedAt)
                      : application.approvedAt 
                        ? formatDate(application.approvedAt)
                        : application.rejectedAt
                          ? formatDate(application.rejectedAt)
                          : formatDate(application.submittedAt)
                  }
                </span>
              </div>
            </div>
            
            {/* Book Your Seat Now Button for Approved Applications - Only for logged-in users viewing their own applications */}
            {application.status === 'Approved' && user && viewMode === 'my-applications' && (
              <div className="w-full overflow-hidden">
                <div className="text-center p-4 rounded-xl bg-gradient-to-r from-blue-400/10 to-purple-400/10 border border-blue-300/20 backdrop-blur-sm overflow-hidden">
                  <div className="mb-3">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      {/* Graduation Cap SVG */}
                      <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
                      </svg>
                      <h4 className={`text-lg font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'} whitespace-nowrap`}>
                        Book Your Seat Now!
                      </h4>
                    </div>
                    <p className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                      Complete your admission process
                    </p>
                    
                    <div className={`flex flex-wrap items-center justify-center gap-4 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-3 overflow-hidden`}>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Shield Check SVG */}
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z"/>
                        </svg>
                        <span className="whitespace-nowrap">Secure Payment</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Badge Check SVG */}
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12,2C17.5,2 22,6.5 22,12C22,17.5 17.5,22 12,22C6.5,22 2,17.5 2,12C2,6.5 6.5,2 12,2M12,4C7.58,4 4,7.58 4,12C4,16.42 7.58,20 12,20C16.42,20 20,16.42 20,12C20,7.58 16.42,4 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"/>
                        </svg>
                        <span className="whitespace-nowrap">Instant Confirmation</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Modern Razorpay Payment Integration */}
                  <div className="w-full max-w-sm mx-auto">
                    <PaymentSection 
                      studentData={application}
                      onPaymentSuccess={onPaymentSuccess}
                      onPaymentFailure={onPaymentFailure}
                    />
                  </div>
                  
                  {/* Support Contact */}
                  <div className={`flex items-center justify-center gap-1 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'} mt-3 overflow-hidden`}>
                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"/>
                    </svg>
                    <span className="text-center break-words">Support: <strong>+91 6364 249 519</strong></span>
                  </div>
                </div>
              </div>
            )}

            {/* Login prompt for non-logged-in users with approved applications */}
            {application.status === 'Approved' && !user && (
              <div className="w-full overflow-hidden">
                <div className="text-center p-4 rounded-xl bg-gradient-to-r from-yellow-400/10 to-orange-400/10 border border-yellow-300/20 backdrop-blur-sm overflow-hidden">
                  <div className="mb-3">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      {/* Login SVG */}
                      <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      <h4 className={`text-lg font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'} whitespace-nowrap`}>
                        Login Required for Payment
                      </h4>
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                      Your application has been approved! Please log in to your account to proceed with seat booking and payment.
                    </p>
                    
                    <Link 
                      to="/"
                      className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all duration-300 transform hover:scale-105 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 shadow-lg hover:shadow-xl`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      Login to Continue
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusCheckPage = ({ darkMode, user, onLogout }) => {
  const [applicationId, setApplicationId] = useState('');
  const [statusResult, setStatusResult] = useState(null);
  const [userApplications, setUserApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState(user ? 'my-applications' : 'search-id');
  const [notification, setNotification] = useState(null);

  // Payment notification functions
  const handlePaymentSuccess = (paymentData) => {
    console.log('Payment successful:', paymentData);
    
    // Show beautiful success notification
    setNotification({
      type: 'success',
      title: 'Payment Successful!',
      message: 'Your seat booking payment has been processed successfully. You will receive confirmation details via email.',
      paymentId: paymentData.razorpay_payment_id
    });
    
    // Here you can update the student's payment status in your database
    // Example: updateStudentPaymentStatus(studentData.id, paymentData);
  };

  const handlePaymentFailure = (error) => {
    console.error('Payment failed:', error);
    
    // Show beautiful error notification
    setNotification({
      type: 'error',
      title: 'Payment Failed!',
      message: 'Your payment could not be processed. Please check your payment details and try again, or contact support if the issue persists.'
    });
  };

  const closeNotification = () => {
    setNotification(null);
  };

  // Real-time listener for user's applications when authenticated
  useEffect(() => {
    let unsubscribe = null;
    let pollInterval = null;

    if (user?.email) {
      console.log('Using API to check user applications for:', user.email);
      setLoading(true);
      setError('');
      
      // Since backend doesn't populate Firestore directly anymore, directly use the API
      loadUserApplicationsViaAPI();
      
      // Set up polling every 30 seconds to stay synced
      pollInterval = setInterval(() => {
        console.log('🔄 Polling for application updates...');
        loadUserApplicationsViaAPI();
      }, 30000);
    } else {
      setLoading(false);
    }

    // Cleanup function
    return () => {
      if (pollInterval) {
        console.log('Cleaning up polling interval');
        clearInterval(pollInterval);
      }
    };
  }, [user?.email]);

  // API-based application loading (fallback when Firebase permissions are restricted)
  const loadUserApplicationsViaAPI = async () => {
    if (!user?.email) return;
    
    setLoading(true);
    setError('');
    
    try {
      console.log('🔍 Loading applications via API for user:', user.email);
      
      // Try the backend API to get all students and filter by email
      const response = await axios.get(`${API_BASE_URL}/api/admin/students`);
      
      if (response.data.success && response.data.students) {
        const userApplications = response.data.students.filter(student => 
          student.email === user.email
        );
        
        const applications = userApplications.map(data => ({
          id: data._id || data.applicationId,
          applicationId: data.applicationId,
          name: data.name,
          email: data.email,
          phone: data.phone,
          status: data.status || 'Pending',
          neetScore: data.neetScore,
          desiredCollege: data.desiredCollege,
          desiredCountry: data.desiredCountry,
          submittedAt: new Date(data.createdAt || data.submittedAt || Date.now()),
          updatedAt: data.updatedAt ? new Date(data.updatedAt) : null,
          approvedAt: data.approvedAt ? new Date(data.approvedAt) : null,
          rejectedAt: data.rejectedAt ? new Date(data.rejectedAt) : null,
          ...data
        }));
        
        console.log('✅ Found applications via API:', applications.length);
        setUserApplications(applications);
        
        if (applications.length === 0) {
          console.log('No applications found for this user via API');
        }
        
      } else {
        throw new Error('API returned invalid response');
      }
      
    } catch (error) {
      console.error('Error loading applications via API:', error);
      setError('Failed to load your applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Legacy one-time fetch function (now used as fallback)
  const loadUserApplications = async () => {
    if (!user?.email) return;
    
    setLoading(true);
    setError('');
    
    try {
      console.log('Loading applications for user (one-time fetch):', user.email);
      
      // Query the students collection using the user's email
      const applicationsQuery = query(
        collection(db, 'students'),
        where('email', '==', user.email)
      );
      
      const querySnapshot = await getDocs(applicationsQuery);
      const applications = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          applicationId: data.applicationId,
          name: data.name,
          email: data.email,
          phone: data.phone,
          status: data.status || 'Pending',
          neetScore: data.neetScore,
          desiredCollege: data.desiredCollege,
          desiredCountry: data.desiredCountry,
          submittedAt: data.createdAt?.toDate?.() || new Date(),
          ...data
        };
      });
      
      console.log('Found applications (one-time):', applications.length);
      setUserApplications(applications);
      
      if (applications.length === 0) {
        console.log('No applications found for this user (one-time)');
      }
      
    } catch (error) {
      console.error('Error loading applications (one-time):', error);
      setError('Failed to load your applications. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckStatus = async (e) => {
    e.preventDefault();
    console.log('🔍 Status check form submitted!', applicationId);
    
    if (!applicationId.trim()) {
      setError('Please enter a valid Application ID.');
      return;
    }
    
    setLoading(true);
    setError('');
    setStatusResult(null);
    console.log('🔍 Starting status check for:', applicationId);

    // Store local cache data for potential fallback (declare outside try block)
    let localCacheData = null;

    try {
      // Make actual API call to backend to get current status
      console.log('📡 Making API call to check status...');
      
      // Check local cache data for potential fallback (don't set it immediately)
      try {
        const adminStudents = localStorage.getItem('admin_dashboard_students');
        const adminArchived = localStorage.getItem('admin_dashboard_archived');
        
        if (adminStudents) {
          const students = JSON.parse(adminStudents);
          const archivedStudents = adminArchived ? JSON.parse(adminArchived) : [];
          
          // Look in both active and archived students
          const allLocalStudents = [...students, ...archivedStudents];
          const localStudent = allLocalStudents.find(s => 
            s.applicationId === applicationId || 
            s._id === applicationId ||
            s.id === applicationId
          );
          
          if (localStudent) {
            console.log('📦 Found student in local admin cache (stored for fallback):', localStudent);
            localCacheData = {
              success: true,
              student: localStudent,
              source: 'local_cache'
            };
          }
        }
      } catch (localError) {
        console.log('📦 Local cache check failed:', localError.message);
      }
      
      try {
        // First try the specific status endpoint
        const statusUrl = `${API_BASE_URL}/api/admin/students/status/${applicationId}`;
        console.log('📡 Trying specific status API URL:', statusUrl);
        
        const response = await axios.get(statusUrl, {
          timeout: 15000
        });
        
        console.log('📡 Status API response:', response.data);
        
        if (response.data && response.data.success && response.data.student) {
          console.log('✅ Status retrieved successfully from status API:', response.data.student);
          setStatusResult({
            ...response.data,
            source: 'backend_status_api'
          });
          setError(''); // Clear any errors
          return; // Success, exit function
        }
      } catch (statusError) {
        console.log('⚠️ Status endpoint not available, trying general students endpoint...');
        console.log('Status API Error:', statusError.response?.status, statusError.message);
        
        // Fallback: Try to get all students and filter locally
        try {
          const allStudentsUrl = `${API_BASE_URL}/api/admin/students`;
          console.log('📡 Trying general students API URL:', allStudentsUrl);
          
          const allStudentsResponse = await axios.get(allStudentsUrl, {
            timeout: 15000
          });
          
          console.log('📡 All students API response:', allStudentsResponse.data);
          
          if (allStudentsResponse.data && allStudentsResponse.data.success && allStudentsResponse.data.students) {
            // Filter to find the specific student
            const students = allStudentsResponse.data.students;
            const student = students.find(s => 
              s.applicationId === applicationId || 
              s._id === applicationId ||
              s.id === applicationId
            );
            
            if (student) {
              console.log('✅ Student found in general students list:', student);
              setStatusResult({
                success: true,
                student: student,
                source: 'backend_all_students'
              });
              setError(''); // Clear any errors
              return; // Success, exit function
            } else {
              console.log('❌ Student not found in students list');
              throw new Error(`Application ID "${applicationId}" not found`);
            }
          } else {
            throw new Error('Invalid response from students endpoint');
          }
        } catch (allStudentsError) {
          console.log('❌ General students endpoint also failed:', allStudentsError);
          throw allStudentsError; // Re-throw to trigger the main error handling
        }
      }
    } catch (err) {
      console.error('❌ API error:', err);
      
      // Show specific error types
      if (err.code === 'ECONNABORTED') {
        console.error('❌ Request timeout - backend may be slow or down');
        setError('Request timeout. The server may be temporarily unavailable. Please try again.');
      } else if (err.response?.status === 404) {
        console.error('❌ Application not found in backend');
        setError(`Application ID "${applicationId}" not found. Please check the ID and try again.`);
      } else if (err.response?.status >= 500) {
        console.error('❌ Server error - falling back to local data');
        setError('Server error. The backend service may be temporarily unavailable.');
      } else if (!err.response) {
        console.error('❌ Network error - no response from server');
        setError('Unable to connect to the server. Please check your internet connection.');
      } else {
        console.error('❌ Unknown error:', err.message);
        setError(err.response?.data?.message || err.message || 'An unknown error occurred.');
      }
      
      // Try local cache data first if available
      if (localCacheData) {
        console.log('🔄 Backend failed, using local cache data as fallback...');
        setStatusResult(localCacheData);
        setError('Showing cached data. Backend temporarily unavailable.');
        return;
      }
      
      // Only fall back to mock data for specific demo/test IDs
      const shouldUseMockData = applicationId.toLowerCase().includes('demo') ||
                               applicationId.toLowerCase().includes('test') ||
                               applicationId.startsWith('OV175') ||
                               applicationId.startsWith('OV174') ||
                               applicationId.startsWith('OV173');
      
      if (shouldUseMockData) {
        console.log('🔄 Using mock data for demo/test application ID...');
        
        // No fallback to mock data - only show real data from API
        console.log('❌ Application ID not found and no mock data fallback available');
        setError(`Application ID "${applicationId}" not found. Please check the ID and try again.`);
      }
    } finally {
      setLoading(false);
      console.log('🔍 Status check completed');
    }
  };  

  // Google login success handler
  const handleGoogleAuthSuccess = async (result) => {
    try {
      console.log('Modern Google Sign-In successful from StatusCheckPage:', result.user);
      setViewMode('my-applications');
    } catch (error) {
      console.error('Google Sign-In Success Handler Error:', error);
      setError('Failed to process Google sign-in. Please try again.');
    }
  };

  // Google login error handler
  const handleGoogleAuthError = (error) => {
    console.error('Google Sign-In Error:', error);
    setError('Failed to sign in with Google. Please try again.');
  };

  // Legacy Google login function (fallback)
  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    
    try {
      console.log('Attempting Google Sign-In from StatusCheckPage...');
      const result = await signInWithPopup(auth, provider);
      console.log('Login successful:', result.user);
      setViewMode('my-applications');
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      setError('Failed to sign in with Google. Please try again.');
    }
  };

  return (
    <div className={`relative ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} pb-20`}>
      {/* Animated background with multiple layers */}
      <div className="absolute inset-0 z-0">
        {/* Primary gradient background */}
        <div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-br from-gray-900 via-blue-900/50 to-purple-900/50' : 'bg-gradient-to-br from-blue-50/80 via-white to-purple-50/80'}`} />
        
        {/* Secondary animated gradient */}
        <div className="absolute inset-0 animated-gradient opacity-20" />
        
        {/* Floating blobs */}
        <div className="floating-blob opacity-30" />
        <div className="floating-blob opacity-25" />
        <div className="floating-blob opacity-35" />
        
        {/* Grid pattern overlay */}
        <div className={`absolute inset-0 opacity-5 ${darkMode ? 'bg-white' : 'bg-gray-900'}`} 
             style={{
               backgroundImage: `radial-gradient(circle, currentColor 1px, transparent 1px)`,
               backgroundSize: '24px 24px'
             }} />
      </div>

      <div className="relative z-10 flex-grow py-6 sm:py-8 px-4">
        <div className="container mx-auto max-w-6xl">
          {/* User Info Header */}
          {user && (
            <div className="mb-6 sm:mb-8">
              <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl border-2 ${darkMode ? 'border-white/10' : 'border-white/30'} backdrop-blur-xl`}>
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 text-center sm:text-left">
                    <div className="relative flex-shrink-0">
                      {user.photoURL ? (
                        <img 
                          src={user.photoURL} 
                          alt="Profile" 
                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-white/30 shadow-lg backdrop-blur-sm"
                        />
                      ) : (
                        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center text-white text-lg sm:text-xl font-bold shadow-lg">
                          {user.displayName?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-green-400 rounded-full border-2 border-white shadow-sm animate-pulse" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className={`text-lg sm:text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} text-break`}>
                        Welcome back, {user.displayName || 'Student'}!
                      </h2>
                      <p className={`text-xs sm:text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} text-break`}>
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onLogout}
                    className={`apple-button ${darkMode ? 'apple-button-dark' : ''} px-4 sm:px-6 py-2 sm:py-3 rounded-xl sm:rounded-2xl font-medium text-xs sm:text-sm transition-all duration-300 hover:scale-105 backdrop-blur-sm flex-shrink-0`}
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl border-2 ${darkMode ? 'border-white/10' : 'border-white/30'} backdrop-blur-xl`}>
            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 bg-gradient-to-r ${darkMode ? 'from-white to-gray-300' : 'from-gray-900 to-gray-600'} bg-clip-text text-transparent text-break px-2`}>
                Application Status Check
              </h1>
              <p className={`text-base sm:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} max-w-2xl mx-auto text-break px-4`}>
                Track your MBBS application progress in real-time with our modern portal
              </p>
              
              {/* Real-time status indicator */}
              {user && (
                <div className="mt-4 flex justify-center">
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium ${
                    darkMode 
                      ? 'bg-green-500/20 text-green-300 border border-green-400/30' 
                      : 'bg-green-50 text-green-700 border border-green-200'
                  }`}>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span>Live updates active</span>
                  </div>
                </div>
              )}
            </div>

            {/* Mode Toggle for authenticated users */}
            {user && (
              <div className="mb-6 sm:mb-8">
                <div className="flex justify-center">
                  <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-xl sm:rounded-2xl p-1 sm:p-2 inline-flex gap-1 sm:gap-2 border ${darkMode ? 'border-white/10' : 'border-white/20'} backdrop-blur-sm`}>
                    <button
                      onClick={() => setViewMode('my-applications')}
                      className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-xs sm:text-sm ${
                        viewMode === 'my-applications'
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg backdrop-blur-sm'
                          : darkMode
                            ? 'text-gray-300 hover:text-white hover:bg-white/10'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'
                      }`}
                    >
                      <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <span className="whitespace-nowrap">My Apps ({userApplications.length})</span>
                    </button>
                    <button
                      onClick={() => setViewMode('search-id')}
                      className={`flex items-center gap-1 sm:gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl font-medium transition-all duration-300 text-xs sm:text-sm ${
                        viewMode === 'search-id'
                          ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg backdrop-blur-sm'
                          : darkMode
                            ? 'text-gray-300 hover:text-white hover:bg-white/10'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-black/5'
                      }`}
                    >
                      <SearchIcon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                      <span className="whitespace-nowrap">Search by ID</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* My Applications View */}
            {user && viewMode === 'my-applications' && (
              <div>
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Spinner />
                    <p className={`mt-4 text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      Loading your applications...
                    </p>
                  </div>
                ) : userApplications.length > 0 ? (
                  <div className="space-y-6">
                    {userApplications.map((application) => (
                      <ApplicationCard 
                        key={application.id} 
                        application={application} 
                        darkMode={darkMode}
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentFailure={handlePaymentFailure}
                        user={user}
                        viewMode={viewMode}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 sm:py-16 px-4">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <SearchIcon className="w-10 h-10 sm:w-12 sm:h-12 text-gray-500" />
                    </div>
                    <h3 className={`text-xl sm:text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'} text-break`}>
                      No applications found
                    </h3>
                    <p className={`text-base sm:text-lg ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 sm:mb-6 text-break px-2`}>
                      Start your journey by submitting your first application!
                    </p>
                    <Link 
                      to="/student-form" 
                      className={`${darkMode ? 'apple-button-dark' : 'apple-button'} px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl font-medium text-sm sm:text-lg transition-all duration-300 hover:scale-105 inline-block`}
                    >
                      Submit New Application
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Search by ID View */}
            {(!user || viewMode === 'search-id') && (
              <div className="max-w-2xl mx-auto">
                <form onSubmit={handleCheckStatus} className="space-y-6">
                  <div>
                    <label 
                      htmlFor="applicationId" 
                      className={`block text-lg font-medium mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}
                    >
                      Application ID
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="applicationId"
                        value={applicationId}
                        onChange={(e) => setApplicationId(e.target.value)}
                        placeholder="Enter your Application ID"
                        className={`apple-input w-full px-6 py-4 rounded-2xl text-lg ${
                          darkMode 
                            ? 'text-white placeholder-gray-400' 
                            : 'text-gray-900 placeholder-gray-500'
                        } focus:outline-none focus:ring-0`}
                        required
                      />
                      <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                        <SearchIcon className="w-6 h-6 text-gray-400" />
                      </div>
                    </div>
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-4 px-8 rounded-2xl font-semibold text-lg transition-all duration-300 ${
                      loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                    }`}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center gap-3">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Checking Status...
                      </div>
                    ) : (
                      'Check Application Status'
                    )}
                  </button>
                </form>

                {error && (
                  <div className={`mt-6 p-4 rounded-2xl border-l-4 ${
                    darkMode 
                      ? 'bg-red-900/20 border-red-500 text-red-300' 
                      : 'bg-red-50 border-red-500 text-red-700'
                  }`}>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center text-white text-sm font-bold">!</div>
                      {error}
                    </div>
                  </div>
                )}

                {statusResult && statusResult.success && (
                  <div className="mt-8">
                    <div className="status-card rounded-3xl p-8">
                      <div className="text-center mb-6">
                        <StatusIcon status={statusResult.student.status} className="w-16 h-16 mx-auto mb-4" />
                        <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          Application Status
                        </h3>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                              Applicant Name
                            </label>
                            <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {statusResult.student.name}
                            </p>
                          </div>
                          <div>
                            <label className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider`}>
                              Submitted Date
                            </label>
                            <p className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {(() => {
                                // Try multiple possible date fields
                                const student = statusResult.student;
                                const possibleDateFields = [
                                  student.submittedAt,
                                  student.createdAt,
                                  student.applicationDate,
                                  student.dateSubmitted,
                                  student.created_at,
                                  student.submitted_at,
                                  student.timestamp
                                ];
                                
                                // Debug: log available fields
                                console.log('🔍 Available student fields:', Object.keys(student));
                                console.log('🗓️ Date fields found:', {
                                  submittedAt: student.submittedAt,
                                  createdAt: student.createdAt,
                                  applicationDate: student.applicationDate,
                                  dateSubmitted: student.dateSubmitted,
                                  created_at: student.created_at,
                                  submitted_at: student.submitted_at,
                                  timestamp: student.timestamp
                                });
                                
                                // Find the first valid date
                                const validDate = possibleDateFields.find(date => date != null);
                                return formatDate(validDate);
                              })()}
                            </p>
                          </div>
                        </div>
                        
                          <div className="text-center py-4">
                          <StatusBadge status={statusResult.student.status} darkMode={darkMode} />
                          <div className={`flex items-center justify-center gap-2 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-500'} mt-3`}>
                            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-green-400 to-blue-500 animate-pulse shadow-sm" />
                            <span>Last updated: {formatDate(new Date())}</span>
                          </div>
                        </div>
                        
                        {statusResult.student.reason && (
                          <div className={`p-4 rounded-2xl ${
                            darkMode ? 'bg-gray-800/50' : 'bg-gray-50'
                          }`}>
                            <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                              {statusResult.student.reason}
                            </p>
                          </div>
                        )}                          {/* Payment section only for users logged in through their own Gmail viewing their own application */}
                        {statusResult.student.status === 'Approved' && user && (viewMode === 'my-applications' || (viewMode === 'search-id' && statusResult.student.email === user.email)) && (
                          <div className="space-y-4">
                            <div className="text-center p-6 rounded-2xl bg-gradient-to-r from-green-400/10 to-blue-400/10 border border-green-300/20">
                              <div className="flex items-center justify-center gap-3 mb-2">
                                <CelebrationIcon className="w-8 h-8" />
                                <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                                  Congratulations! Your application has been approved. Please contact our support team for next steps.
                                </p>
                              </div>
                            </div>
                            
                            {/* Book Your Seat Now Section */}
                            <div className="w-full overflow-hidden">
                              <div className="text-center p-6 rounded-2xl bg-gradient-to-r from-blue-400/10 to-purple-400/10 border border-blue-300/20 overflow-hidden">
                                <div className="mb-4">
                                  <div className="flex items-center justify-center gap-2 mb-2">
                                    {/* Graduation Cap SVG */}
                                    <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12 3L1 9l4 2.18v6L12 21l7-3.82v-6l2-1.09V17h2V9L12 3zm6.82 6L12 12.72 5.18 9 12 5.28 18.82 9zM17 15.99l-5 2.73-5-2.73v-3.72L12 15l5-2.73v3.72z"/>
                                    </svg>
                                    <h3 className={`text-xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'} whitespace-nowrap`}>
                                      Secure Your Seat Now!
                                    </h3>
                                  </div>
                                  <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
                                    Your application has been approved! Complete your seat booking to confirm your admission and begin your medical journey.
                                  </p>
                                  <div className={`flex flex-wrap items-center justify-center gap-4 text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-4 overflow-hidden`}>
                                    <span className="flex items-center gap-1 flex-shrink-0">
                                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M10,17L6,13L7.41,11.59L10,14.17L16.59,7.58L18,9L10,17Z"/>
                                      </svg>
                                      Secure Payment Gateway
                                    </span>
                                    <span className="flex items-center gap-1 flex-shrink-0">
                                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12,2C17.5,2 22,6.5 22,12C22,17.5 17.5,22 12,22C6.5,22 2,17.5 2,12C2,6.5 6.5,2 12,2M12,4C7.58,4 4,7.58 4,12C4,16.42 7.58,20 12,20C16.42,20 20,16.42 20,12C20,7.58 16.42,4 12,4M11,16.5L6.5,12L7.91,10.59L11,13.67L16.59,8.09L18,9.5L11,16.5Z"/>
                                      </svg>
                                      Instant Confirmation
                                    </span>
                                    <span className="flex items-center gap-1 flex-shrink-0">
                                      <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12,12A3,3 0 0,1 9,9A3,3 0 0,1 12,6A3,3 0 0,1 15,9A3,3 0 0,1 12,12M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z"/>
                                      </svg>
                                      24/7 Support
                                    </span>
                                  </div>
                                </div>
                                
                                {/* Modern Razorpay Payment Integration */}
                                <div className="w-full max-w-sm mx-auto mb-4">
                                  <PaymentSection 
                                    studentData={statusResult.student}
                                    onPaymentSuccess={handlePaymentSuccess}
                                    onPaymentFailure={handlePaymentFailure}
                                  />
                                </div>
                                
                                <div className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'} mt-4 overflow-hidden break-words`}>
                                  <div className="flex items-center justify-center gap-1 mb-2">
                                    <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M12,2A2,2 0 0,1 14,4C14,6.21 12.21,8 10,8H9V7.5A0.5,0.5 0 0,0 8.5,7A0.5,0.5 0 0,0 8,7.5V8H7A2,2 0 0,1 5,6V4A2,2 0 0,1 7,2H12M12,9A3,3 0 0,1 15,12A3,3 0 0,1 12,15A3,3 0 0,1 9,12A3,3 0 0,1 12,9Z"/>
                                    </svg>
                                    <strong>Note:</strong> After successful payment, you will receive confirmation details via email and SMS.
                                  </div>
                                  <div className="flex items-center justify-center gap-1">
                                    <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                      <path d="M6.62,10.79C8.06,13.62 10.38,15.94 13.21,17.38L15.41,15.18C15.69,14.9 16.08,14.82 16.43,14.93C17.55,15.3 18.75,15.5 20,15.5A1,1 0 0,1 21,16.5V20A1,1 0 0,1 20,21A17,17 0 0,1 3,4A1,1 0 0,1 4,3H7.5A1,1 0 0,1 8.5,4C8.5,5.25 8.7,6.45 9.07,7.57C9.18,7.92 9.1,8.31 8.82,8.59L6.62,10.79Z"/>
                                    </svg>
                                    For any assistance, contact us at <strong>+91 6364 249 519</strong>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Congratulations message for approved applications when checking manually or not logged in */}
                        {statusResult.student.status === 'Approved' && (!user || (viewMode === 'search-id' && statusResult.student.email !== user.email)) && (
                          <div className="space-y-4">
                            <div className="text-center p-6 rounded-2xl bg-gradient-to-r from-green-400/10 to-blue-400/10 border border-green-300/20">
                              <div className="flex items-center justify-center gap-3 mb-2">
                                <CelebrationIcon className="w-8 h-8" />
                                <p className="text-lg font-semibold text-green-600 dark:text-green-400">
                                  Congratulations! This application has been approved.
                                </p>
                              </div>
                              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'} mt-2`}>
                                To proceed with seat booking and payment, please log in to your account with the email address associated with this application.
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Login prompt for non-logged-in users with approved applications */}
                        {statusResult.student.status === 'Approved' && !user && (
                          <div className="space-y-4">
                            <div className="text-center p-6 rounded-2xl bg-gradient-to-r from-yellow-400/10 to-orange-400/10 border border-yellow-300/20">
                              <div className="flex items-center justify-center gap-3 mb-4">
                                <svg className="w-8 h-8 text-yellow-600 dark:text-yellow-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                </svg>
                                <h3 className={`text-xl font-bold ${darkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                                  Login Required for Payment
                                </h3>
                              </div>
                              <p className={`text-lg ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                                Congratulations! Your application has been approved. Please log in to your account to proceed with seat booking and payment.
                              </p>
                              <Link 
                                to="/"
                                className={`inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-semibold text-white text-lg transition-all duration-300 transform hover:scale-105 bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 shadow-lg hover:shadow-xl`}
                              >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                </svg>
                                Login to Continue
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}            {/* Action-oriented Help Section */}
            {(!user || viewMode === 'search-id') && !statusResult && (
              <div className="mt-8 max-w-4xl mx-auto">
                <div className={`glass ${darkMode ? 'glass-dark' : ''} rounded-2xl p-6 border border-white/20 backdrop-blur-xl`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Modern Google Login for My Applications */}
                    <div className="text-center">
                      <button
                        onClick={handleGoogleLogin}
                        className="w-full glass glass-button rounded-xl p-4 hover:scale-105 transition-all duration-300 border border-blue-500/30 hover:border-blue-500/50 group"
                      >
                        <div className="flex items-center justify-center mb-2">
                          <UserIcon className="w-8 h-8 opacity-80 group-hover:opacity-100 transition-opacity text-blue-500" />
                        </div>
                        <span className={`text-sm font-medium block ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          My Applications
                        </span>
                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          Login with Google to view your applications
                        </p>
                      </button>
                    </div>                    {/* WhatsApp Support */}
                    <div className="text-center">
                      <a
                        href="https://wa.me/916364249519?text=Hi, I need help with my MBBS application status"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full glass glass-button rounded-xl p-4 hover:scale-105 transition-all duration-300 border border-green-500/30 hover:border-green-500/50 group block"
                      >
                        <svg className="w-8 h-8 mx-auto mb-2 opacity-80 group-hover:opacity-100 transition-opacity" viewBox="0 0 24 24">
                          <defs>
                            <linearGradient id="whatsappGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#25D366" />
                              <stop offset="100%" stopColor="#128C7E" />
                            </linearGradient>
                          </defs>
                          <path fill="url(#whatsappGradient)" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.465 3.516"/>
                        </svg>
                        <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          WhatsApp
                        </span>                        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          +91 6364 249 519
                        </p>
                      </a>
                    </div>
                  </div>
                  
                  <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-white/10' : 'border-gray-200/50'} text-center`}>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      💡 Application IDs start with "OV" followed by numbers
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Beautiful Payment Notification - Rendered at page level */}
      {notification && (
        <PaymentNotification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          paymentId={notification.paymentId}
          onClose={closeNotification}
          autoClose={true}
          duration={8000} // 8 seconds for payment notifications
        />
      )}
    </div>
  );
};

export default StatusCheckPage;
